import { useAuth0 } from '@auth0/auth0-react';
import { Site, SiteSearchFiltersInput } from '@domain/graphql.types';
import { SitesRequest, toSitesQueryString } from '@infrastructure/http';
import { useCallback, useEffect, useState } from 'react';

type SitesResponse = { query: string; attempt: number; data?: Site[]; error: boolean };

/**
 * All the sites of GET /sites, optionally restricted by search filters.
 * `data` only ever holds the response of the current filters: it is undefined while they are loading or after a failure,
 * so sites of a previous filter set are never presented as matching the new one.
 * `loading` is true until the response of the current filters (and attempt) arrives, `error` when that request failed,
 * and `retry` runs the current request again.
 */
const useSitesRequest = (filters?: SiteSearchFiltersInput | null) => {
  const query = toSitesQueryString(filters);
  const [attempt, setAttempt] = useState(0);
  const [response, setResponse] = useState<SitesResponse>();

  const { getAccessTokenSilently } = useAuth0();

  // biome-ignore lint/correctness/useExhaustiveDependencies: keyed on the query string, an equal filter set in a new object must not refetch
  useEffect(() => {
    // Aborted when the query changes, on retry or on unmount (also React StrictMode's throwaway effect run in dev):
    // a superseded request is cancelled, or never sent when it is superseded while the token is fetched.
    const controller = new AbortController();
    const { signal } = controller;

    const execute = async () => {
      try {
        const token = await getAccessTokenSilently();
        if (signal.aborted) return;

        const result = await fetch(SitesRequest(token, filters), { signal });
        const data = result.ok ? ((await result.json()) as Site[]) : undefined;

        if (!signal.aborted) setResponse({ query, attempt, data, error: !result.ok });
      } catch {
        // An aborted request is superseded, not failed.
        if (!signal.aborted) setResponse({ query, attempt, error: true });
      }
    };

    execute();

    return () => controller.abort();
  }, [query, attempt]);

  const retry = useCallback(() => setAttempt((current) => current + 1), []);

  const isCurrentQuery = response?.query === query;
  const isCurrentAttempt = isCurrentQuery && response.attempt === attempt;

  return {
    // A retry of the same filters keeps their sites, if any, until the new response arrives.
    data: isCurrentQuery ? response.data : undefined,
    loading: !isCurrentAttempt,
    error: isCurrentAttempt && response.error,
    retry,
  };
};

export default useSitesRequest;
